import {
  CloseCircleOutlined,
  MoreOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Dropdown,
  Flex,
  Input,
  Menu,
  Modal,
  Skeleton,
  Tooltip,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchArchiveSessionsList,
  fetchSessionsList,
} from "../../../store/collecteazeAiSlice";
import "./HistoryComp.scss";
import { updateChatSessionName } from "../../../assets/api/collecteazeAi";
import toast from "react-hot-toast";

const { Text } = Typography;

// Truncate helper
const truncateText = (text, maxLength = 28) => {
  if (text.length <= maxLength) return text;
  return (
    <Tooltip title={text}>
      {text.substring(0, maxLength - 3) + "..."}
    </Tooltip>
  );
};

// Date category helper
const getDateCategory = (timestamp) => {
  const isValidTimestamp = !isNaN(timestamp) && timestamp > 0;
  if (!isValidTimestamp) return "Today";
  const messageDate = new Date(timestamp);
  if (isNaN(messageDate.getTime())) return "Today";
  const today = new Date();
  const diffInDays = Math.floor(
    (today - messageDate) / (1000 * 60 * 60 * 24)
  );
  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "Yesterday";
  if (diffInDays >= 2 && diffInDays <= 7) return "Past 7 Days";
  if (diffInDays >= 8 && diffInDays <= 30) return "Previous 30 Days";
  const month = messageDate.toLocaleString("default", { month: "long" });
  const year = messageDate.getFullYear();
  if (year === today.getFullYear()) return month;
  return year.toString();
};

const HistoryComp = ({
  onClose,
  onNewChat,
  onSelectSession,
  onRenameSession,
  onArchiveSession,
  onUnarchiveSession,
  onDeleteSession,
  showArchived,
  setShowArchived,
  archiveLoading,
}) => {
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [newSessionTitle, setNewSessionTitle] = useState("");
  const { token, user } = useSelector((state) => state.auth);
  const {
    sessions_loading,
    all_sessions = [],
    archived_sessions_loading,
    all_archived_sessions = [],
  } = useSelector((state) => state.collecteazeAi);
  const { current_session } = useSelector(
    (state) => state.collecteazeAi
  );
  const [load, setLoad] = useState({ rename: false, delete: false });

  const dispatch = useDispatch();

  const handleRename = (sessionId, currentTitle) => {
    setCurrentSessionId(sessionId);
    setNewSessionTitle(currentTitle);
    setIsRenameModalVisible(true);
  };

  const handleRenameSubmit = async () => {
    if (newSessionTitle.trim()) {
      onRenameSession(currentSessionId, newSessionTitle.trim());
    }
    setCurrentSessionId(null);
    setNewSessionTitle("");
    setLoad((prev) => ({ ...prev, rename: true }));
    try {
      const res = await updateChatSessionName({
        session_id: currentSessionId,
        new_title: newSessionTitle.trim(),
        token,
      });
      if (res.status === 200) {
        toast.success(
          res?.data?.message || "Session renamed successfully"
        );
        dispatch(fetchSessionsList({ user_id: user?.email }));
        setIsRenameModalVisible(false);
      } else {
       
        setIsRenameModalVisible(false);
      }
    } catch (error) {
      setIsRenameModalVisible(false);
    } finally {
      setLoad((prev) => ({ ...prev, rename: false }));
    }
  };

  const handleRenameCancel = () => {
    setIsRenameModalVisible(false);
    setCurrentSessionId(null);
    setNewSessionTitle("");
  };

  const menu = (sessionId, title, isArchived) => (
    <Menu className="options_Box">
      <Menu.Item key="rename" onClick={() => handleRename(sessionId, title)}>
        Rename
      </Menu.Item>
      {isArchived ? (
        <Menu.Item
          key="unarchive"
          onClick={() => onArchiveSession(sessionId, false)}
        >
          Unarchive
        </Menu.Item>
      ) : (
        <Menu.Item
          key="archive"
          onClick={() => onArchiveSession(sessionId, true)}
        >
          {archiveLoading ? (
            <Skeleton.Button active size="small" />
          ) : (
            "Archive"
          )}
        </Menu.Item>
      )}
      <Menu.Item key="delete" onClick={() => onDeleteSession(sessionId)}>
        Delete
      </Menu.Item>
    </Menu>
  );

  useEffect(() => {
    dispatch(fetchSessionsList({ user_id: user?.email }));
    dispatch(fetchArchiveSessionsList({ user_id: user?.email }));
  }, [dispatch, current_session]);

  return (
    <Flex className="mainWrapper">
      <Card className="historyCompMain">
        <Flex className="title_box">
          <Text>History</Text>
          <CloseCircleOutlined
            style={{ cursor: "pointer" }}
            onClick={onClose}
          />
        </Flex>

        <Flex className="new_chat_box">
          <Button className="newChat_btn" onClick={onNewChat}>
            <PlusCircleOutlined /> New Chat
          </Button>
        </Flex>

        <Flex style={{ padding: "8px 16px", justifyContent: "center" }}>
          <Button type="link" onClick={() => setShowArchived(!showArchived)}>
            {showArchived
              ? "Show Active Sessions"
              : "Show Archived Sessions"}
          </Button>
        </Flex>

        <Card className="historyDetailsBox">
          {(Array.isArray(showArchived ? all_archived_sessions : all_sessions)
    ? (showArchived ? all_archived_sessions : all_sessions)
    : []
  )?.map(
            (session) => (
              <Flex
                key={session.session_id}
                className={`detailedInfo session_list_item ${
                  current_session === session?.session_id
                    ? "active-session"
                    : ""
                }`}
                style={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  opacity: showArchived ? 0.5 : 1,
                  cursor: showArchived ? "not-allowed" : "pointer",
                }}
              >
                {sessions_loading || archived_sessions_loading ? (
                  <Skeleton.Input size="small" active />
                ) : (
                  <Text
                    style={{ flex: 1 }}
                    onClick={() => {
                      if (showArchived) {
                        toast.error(
                          "Please unarchive this session before opening it."
                        );
                      } else {
                        onSelectSession(session.session_id);
                      }
                    }}
                  >
                    {truncateText(session.title)}
                  </Text>
                )}

                <Dropdown
                  overlay={menu(
                    session.session_id,
                    session.title,
                    showArchived
                  )}
                  trigger={["click"]}
                >
                  <Button
                    type="text"
                    icon={<MoreOutlined />}
                    style={{ padding: 0 }}
                    aria-label="More options"
                  />
                </Dropdown>
              </Flex>
            )
          )}

          {(showArchived ? all_archived_sessions : all_sessions)?.length ===
            0 && (
            <Flex className="wrapper_box">
              <Text>
                {showArchived
                  ? "No archived sessions."
                  : "No chat history available."}
              </Text>
            </Flex>
          )}
        </Card>
      </Card>

      <Modal
        title="Rename Chat Session"
        open={isRenameModalVisible}
        onOk={handleRenameSubmit}
        onCancel={handleRenameCancel}
        okText="Save"
        cancelText="Cancel"
        confirmLoading={load.rename}
      >
        <Input
          value={newSessionTitle}
          onChange={(e) => setNewSessionTitle(e.target.value)}
          placeholder="Enter new session name"
        />
      </Modal>
    </Flex>
  );
};

export default HistoryComp;
